import { useCallback, useEffect, useState } from 'react';

import { isFedRamp } from '@atlassian/atl-context';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition';

import { useReverseTrialEligible } from './useReverseTrialEligible';
import { ReverseTrialCohort } from './__types__/InstanceIsInReverseTrialQuery';
import { useTrialDetails } from './useTrialDetails';

export enum Cohort {
	NOT_ENROLLED = 'not-enrolled',
	CONTROL = 'control',
	TEST = 'test',
}

const COHORT = 'cohort';

type HookType = {
	isPremiumOnboardingEnabled?: boolean;
	shouldCheckIsInTrial?: boolean;
};

export const PTO_STATSIG_EXPERIMENT = 'cc_editions_premium_trial_onboarding_experiment';

export const PTO_RT_STATSIG_EXPERIMENT = 'cc_editions_premium_trial_onboarding_rt_experiment';

export const useIsPremiumOnboardingFeatureDiscoveryEnabled = ({
	isPremiumOnboardingEnabled = true,
	shouldCheckIsInTrial = true,
}: HookType = {}) => {
	const { edition, isLoggedIn, isLicensed, userId } = useSessionData();
	const [isPTOEligible, setIsPTOEligible] = useState<boolean>(false);
	const [isPTORTEligible, setIsPTORTEligible] = useState<boolean>(false);
	const [ptoFlagValue, setPTOFlagValue] = useState<string>(Cohort.NOT_ENROLLED);
	const [ptoRTFlagValue, setPTORTFlagValue] = useState<string>(Cohort.NOT_ENROLLED);
	const [isFlagLoading, setIsFlagLoading] = useState<boolean>(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isFedrampEnv = isFedRamp();

	const isUserAndInstanceEligible =
		isPremiumOnboardingEnabled &&
		edition === ConfluenceEdition.PREMIUM &&
		isLoggedIn &&
		isLicensed &&
		Boolean(userId) &&
		// FedRamp shouldn't have any premium trial customer but we are excluding it just in case
		!isFedrampEnv;

	const {
		loading: reverseTrialLoading,
		error: reverseTrialError,
		reverseTrialCohort,
	} = useReverseTrialEligible({ skip: !isUserAndInstanceEligible });

	/**
	 * Checking pre-requsite for the experiment, and skip all other checks if instance/user is not eligible.
	 * Exclude all Reverse Trial traffic, this includes both CONTROL and VARIANT for RT
	 * Only call Statsig when instance/user is eligible.
	 */
	useEffect(() => {
		const isReverseTrialEnrolled = reverseTrialCohort === ReverseTrialCohort.ENROLLED;
		if (isUserAndInstanceEligible && !reverseTrialLoading && !reverseTrialError) {
			const ptoFlag = FeatureGates.getExperimentValue<string>(
				PTO_STATSIG_EXPERIMENT,
				COHORT,
				Cohort.NOT_ENROLLED,
				{
					fireExperimentExposure: false,
				},
			);

			const ptoRTFlag = FeatureGates.getExperimentValue<string>(
				PTO_RT_STATSIG_EXPERIMENT,
				COHORT,
				Cohort.NOT_ENROLLED,
			);

			if (!isReverseTrialEnrolled || (isReverseTrialEnrolled && ptoRTFlag === Cohort.TEST)) {
				setIsPTOEligible(true);
			}
			setPTOFlagValue(ptoFlag);

			if (isReverseTrialEnrolled) {
				setIsPTORTEligible(true); //PTO+RT epxeriment only applies to RT enrolled instances
			}
			setPTORTFlagValue(ptoRTFlag);
		}
		if (!reverseTrialLoading) setIsFlagLoading(false);
	}, [isUserAndInstanceEligible, reverseTrialCohort, reverseTrialError, reverseTrialLoading]);

	const shouldSkipQueryResult = !shouldCheckIsInTrial || ptoFlagValue !== Cohort.TEST;

	const { trialData, trialDetailsError, trialDetailsLoading, trialDaysLeft, isInstanceInTrial } =
		useTrialDetails({
			skip: !shouldCheckIsInTrial || !isUserAndInstanceEligible,
		});

	const isEligibleWithQueryResult =
		isPTOEligible &&
		(shouldSkipQueryResult || (!trialDetailsLoading && !trialDetailsError && isInstanceInTrial));

	const isPremiumOnboardingFeatureDiscoveryEnabled =
		isEligibleWithQueryResult && ptoFlagValue === Cohort.TEST;

	const isPTORTEnabled =
		isPTORTEligible && isPremiumOnboardingFeatureDiscoveryEnabled && ptoRTFlagValue === Cohort.TEST;

	const firePremiumOnboardingFeatureExposure = useCallback(() => {
		if (isPTOEligible && ptoFlagValue && ptoFlagValue !== Cohort.NOT_ENROLLED) {
			// Manually fire Statsig exposure event
			FeatureGates.manuallyLogExperimentExposure(PTO_STATSIG_EXPERIMENT);

			// Requested by data to manually fire GASV3 track event alongside statsig feature exposure event to allow post analysis on Socrates directly.
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'feature',
					attributes: {
						flagKey: PTO_STATSIG_EXPERIMENT,
						value: ptoFlagValue,
						isPTORTEnabled,
					},
					source: 'premTrialActionScreen',
				},
			}).fire();
		}
	}, [createAnalyticsEvent, ptoFlagValue, isPTOEligible, isPTORTEnabled]);

	return {
		loading: trialDetailsLoading || reverseTrialLoading || isFlagLoading,
		inTrialError: trialDetailsError,
		reverseTrialError,
		isPremiumOnboardingFeatureDiscoveryEnabled,
		isPremiumOnboardingFeatureDiscoveryEligible: isPTOEligible,
		cohort: ptoFlagValue,
		firePremiumOnboardingFeatureExposure,
		isPTORTEnabled,
		trialDaysLeft,
		trialEndDate: trialData?.license?.trialEndDate,
	};
};
